import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { MetricCard } from "@/components/dashboard/MetricCardV2";
import { HeatMapModule } from "@/components/dashboard/HeatMapModule";
import { LaneSummaryCard } from "@/components/dashboard/LaneSummaryCard";
import { MyTrucksTableV2 } from "@/components/my-trucks/MyTrucksTableV2";
import { AIQuickActionsPanel } from "@/components/dashboard/AIQuickActionsPanel";
import { LiveFeedV2 } from "@/components/dashboard/LiveFeedV2";
import { AIRecommendationsPanel } from "@/components/dashboard/AIRecommendationsPanel";
import { AtlasAssistantPanel } from "@/components/dashboard/AtlasAssistantPanel";
import { StaleTruckPostsPanel } from "@/components/dashboard/StaleTruckPostsPanel";
import { HotLanesPanel } from "@/components/dashboard/HotLanesPanel";
import { MarketIntelligencePane } from "@/components/dashboard/MarketIntelligencePane";
import { CarrierPostDuplicatesPane } from "@/components/dashboard/CarrierPostDuplicatesPane";
import { TruckExpirationPane } from "@/components/dashboard/TruckExpirationPane";
import ManualPendingBanner from "@/components/onboarding/ManualPendingBanner";

import { CarrierLoadMatchesPane } from "@/components/dashboard/CarrierLoadMatchesPane";
import { CarrierLaneVolumePane } from "@/components/dashboard/CarrierLaneVolumePane";
import { GatedSection, UpgradeOverlay } from "@/components/dashboard/UpgradeGate";
import { DocumentUploadGate } from "@/components/dashboard/DocumentUploadGate";
import { DemoHint } from "@/components/demo/DemoHint";

import { useAuth } from "@/contexts/AuthContext";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useCarrierDashboardStats } from "@/hooks/useCarrierDashboardStats";
import { useLaneData } from "@/hooks/useLaneData";
import { useMarketPressureIndex } from "@/hooks/useMarketPressureIndex";
import { useMyTrucks } from "@/hooks/useMyTrucks";

export default function CarrierDashboardV2() {
  useEffect(() => {
    const main = document.querySelector("main");
    if (main) main.scrollTop = 0;
  }, []);
  usePageMeta({ title: "Carrier Dashboard | LaneRunner", description: "LaneRunner carrier command center" });
  const navigate = useNavigate();
  const stats = useCarrierDashboardStats();
  const { trucks } = useMyTrucks();
  const [selectedEquipment, setSelectedEquipment] = useState<"Van" | "Reefer" | "Flatbed">("Van");
  const [selectedHaulType, setSelectedHaulType] = useState<"short" | "medium" | "long" | null>(null);
  const [centerView, setCenterView] = useState<"trucks" | "market" | "patterns" | "expiring" | "load-matches" | "lane-volume">("trucks");
  const HAUL_RANGES: Record<string, { min: number; max: number }> = { short: { min: 188, max: 500 }, medium: { min: 501, max: 1500 }, long: { min: 1501, max: 4000 } };
  const EQUIPMENT_DB_MAP: Record<string, string> = { Van: "van", Reefer: "reefer", Flatbed: "flatbed" };
  const haulRange = selectedHaulType ? HAUL_RANGES[selectedHaulType] : null;
  const { lanes, globalAverage, isLoading: lanesLoading } = useLaneData(EQUIPMENT_DB_MAP[selectedEquipment], haulRange?.min, haulRange?.max);

  // Market Pressure Index (parity with broker)
  const { score: matchScore, label: matchLabel, labelColor: matchLabelColor } = useMarketPressureIndex(selectedEquipment);

  const isLoading = stats.isLoading || lanesLoading;

  useEffect(() => {
    if (!isLoading) {
      const main = document.querySelector("main");
      if (main) main.scrollTop = 0;
    }
  }, [isLoading]);

  return (
    <>
      <UpgradeOverlay />
      <DocumentUploadGate />
      <div className="flex flex-col gap-2 xl:gap-3 w-full">
        <ManualPendingBanner />



        {/* ── Metrics Row ──────────────────────────────── */}
        <GatedSection>
          <div className="flex flex-nowrap sm:grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-2 xl:gap-3 overflow-x-auto sm:overflow-visible pb-1 sm:pb-0 snap-x snap-mandatory [&>*]:snap-start [&>*]:min-w-[150px] sm:[&>*]:min-w-0 [&>*]:h-full">
            <MetricCard
              label="Posted Trucks"
              value={stats.trucksPosted}
              sub={`${stats.trucksOpen} open, ${stats.trucksExpired} expired`}
              isLoading={stats.isLoading}
              onClick={() => navigate("/my-trucks")}
            />
            <MetricCard
              label="Platform Loads"
              value={(stats.activeLoadsOnboard * 3).toLocaleString()}
              sub="Live marketplace volume"
              isLoading={stats.isLoading}
            />
            <MetricCard
              label="Avg RPM"
              value={globalAverage > 0 ? `$${globalAverage.toFixed(2)}` : "—"}
              sub={`${lanes.length} active lanes`}
              isLoading={lanesLoading}
            />
            <MetricCard
              label="Diesel Fuel Avg"
              value="$5.58"
              sub="U.S. Fuel Price/Gallon"
              badge={<span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-primary">▲ 1.43%</span>}
            />
            <MetricCard
              label="Market Pressure Index"
              value={`${matchScore}%`}
              sub="True supply vs demand with weighting"
              badge={<span className={`text-[10px] font-semibold ${matchLabelColor}`}>{matchLabel}</span>}
            />
          </div>
        </GatedSection>

        {/* ── Main 3-Column Grid ────────────────────────── */}
        <GatedSection>
          <div className="grid grid-cols-1 lg:grid-cols-[220px_1fr_240px] xl:grid-cols-[260px_1fr_300px] gap-3 xl:gap-4 items-stretch [&>*:nth-child(1)]:order-1 [&>*:nth-child(2)]:order-3 [&>*:nth-child(3)]:order-2 lg:[&>*:nth-child(2)]:order-2 lg:[&>*:nth-child(3)]:order-3">
            {/* LEFT — Heat Map + Lane Summary */}
            <div className="flex flex-col gap-0 rounded-lg border bg-card overflow-hidden">
              <div className="flex items-center gap-1.5 border-b px-3 py-2.5 bg-muted/20">
                <span className="text-[10px] text-accent">⊕</span>
                <DemoHint hintKey="section.rpmMarketIndex" side="right" showIndicator>
                <h4 className="text-caption font-bold text-foreground">RPM Market Index</h4>
                </DemoHint>
              </div>
              <div className="px-3 pt-2 pb-0">
                <HeatMapModule
                  equipment={selectedEquipment}
                  onEquipmentChange={setSelectedEquipment}
                  haulType={selectedHaulType}
                  onHaulTypeChange={setSelectedHaulType}
                  lanes={lanes}
                  globalAverage={globalAverage}
                  isLoading={lanesLoading}
                />
              </div>
              {lanes.slice(0, 2).map((lane, i) => (
                <div key={i} className="border-t mx-0 p-0">
                  <LaneSummaryCard origin={lane.shortName.split(' → ')[0] || '—'} destination={lane.shortName.split(' → ')[1] || '—'} avgRpm={lane.rate} rpmChange={0} postedLoads={lane.volume} equipment={EQUIPMENT_DB_MAP[selectedEquipment]} />
                </div>
              ))}
              {lanes.length === 0 && !lanesLoading && (
                <div className="border-t p-3 text-center text-[11px] text-muted-foreground">No lane data available</div>
              )}
            </div>

            {/* CENTER — My Posted Trucks OR one of the AI surfaces */}
            <div className="flex flex-col gap-0 h-full">
              {centerView === "market" ? (
                <MarketIntelligencePane onBack={() => setCenterView("trucks")} />
              ) : centerView === "patterns" ? (
                <CarrierPostDuplicatesPane onBack={() => setCenterView("trucks")} />
              ) : centerView === "expiring" ? (
                <TruckExpirationPane onBack={() => setCenterView("trucks")} trucks={trucks} />
              ) : centerView === "load-matches" ? (
                <CarrierLoadMatchesPane onBack={() => setCenterView("trucks")} trucks={trucks} />
              ) : centerView === "lane-volume" ? (
                <CarrierLaneVolumePane
                  onBack={() => setCenterView("trucks")}
                  lanes={lanes}
                  globalAverage={globalAverage}
                  equipment={EQUIPMENT_DB_MAP[selectedEquipment]}
                  isLoading={lanesLoading}
                />
              ) : (
                <MyTrucksTableV2 />
              )}
            </div>

            {/* RIGHT — AI Quick Actions (carrier mode) */}
            <div className="flex flex-col h-full rounded-lg border bg-card overflow-hidden" style={{ boxShadow: "var(--shadow-sm)" }}>
              <div className="flex items-center justify-between border-b px-3 py-2.5 bg-muted/20">
                <DemoHint hintKey="section.aiQuickActions" side="left" showIndicator>
                <h4 className="text-caption font-bold text-foreground">AI Quick Actions</h4>
                </DemoHint>
              </div>
              <div className="flex-1 p-2.5">
                <AIQuickActionsPanel
                  mode="carrier"
                  onMarketIntelligence={() => setCenterView("market")}
                  onPatternDetector={() => setCenterView("patterns")}
                  onTruckExpiration={() => setCenterView("expiring")}
                  onLoadMatching={() => setCenterView("load-matches")}
                  onLaneVolume={() => setCenterView("lane-volume")}
                />
              </div>
            </div>
          </div>
        </GatedSection>

        {/* ── Live Feed ─────────────────────────────────── */}
        <GatedSection>
          <LiveFeedV2 />
        </GatedSection>

        {/* ── Lower Row: 4-column carrier command center ── */}
        <GatedSection>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3 xl:gap-4 items-stretch">
            <AIRecommendationsPanel
              mode="carrier"
              equipment={EQUIPMENT_DB_MAP[selectedEquipment]}
              minMiles={haulRange?.min}
              maxMiles={haulRange?.max}
              lanes={lanes}
              isLoading={lanesLoading}
            />
            <StaleTruckPostsPanel trucks={trucks} />
            <HotLanesPanel lanes={lanes} equipment={selectedEquipment} isLoading={lanesLoading} />
            <AtlasAssistantPanel hasLoads={trucks.length > 0} />
          </div>
        </GatedSection>

      </div>
    </>
  );
}
